import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, Laptop, Gamepad2, Watch, Headphones, TrendingUp, TrendingDown, Star, Award, Sparkles, Package, Clock } from 'lucide-react';
import ProductCard from './ProductCard';
import './HomePage.css';
import { apiRequest, publicApiRequest, conditionalApiRequest, API_CONFIG } from '../config/api';
import { cartService } from '../services/cartService';
import { useFeaturedProducts } from '../hooks/useFeaturedProducts';
import { useBestSellers } from '../hooks/useBestSellers';
import { useNewArrivals } from '../hooks/useNewArrivals';
import { useToast } from '../hooks/useToast';

const HomePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('featured');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [banners, setBanners] = useState<any[]>([]);
  const [bannersLoading, setBannersLoading] = useState<boolean>(true);
  const [bannersError, setBannersError] = useState<string | null>(null);
  const [categoryMeta, setCategoryMeta] = useState<Record<string, { total_items: number; trend?: string }>>({});
  const [categoryNames, setCategoryNames] = useState<Record<string, string>>({});
  const [wishlist, setWishlist] = useState<number[]>([]); // NEW
  const [cart, setCart] = useState<Record<number, number>>({}); // NEW

  // Deal of the Day states
  const [deal, setDeal] = useState<any>(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [dealLoading, setDealLoading] = useState<boolean>(true);
  const [dealError, setDealError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { showError } = useToast();

  const sortBannersNewestFirst = (items: any[]) => {
    return [...items].sort((a, b) => {
      const aDate = a?.uploaded_at || a?.created_at || a?.createdAt || a?.updated_at;
      const bDate = b?.uploaded_at || b?.created_at || b?.createdAt || b?.updated_at;
      const aTime = aDate ? new Date(aDate).getTime() : NaN;
      const bTime = bDate ? new Date(bDate).getTime() : NaN;

      if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
        return bTime - aTime;
      }

      return Number(b?.id || 0) - Number(a?.id || 0);
    });
  };

  // Use the new hooks for product data
  const { products: featuredProducts, loading: featuredLoading, error: featuredError } = useFeaturedProducts();
  const { products: bestSellers, loading: bestSellersLoading, error: bestSellersError } = useBestSellers();
  const { products: newArrivals, loading: newArrivalsLoading, error: newArrivalsError } = useNewArrivals();

  const normalizeDeal = (rawDeal: any) => {
    if (!rawDeal) return null;

    return {
      ...rawDeal,
      title: rawDeal.title || rawDeal.product?.name || 'Deal of the Day',
      subtitle: rawDeal.subtitle || rawDeal.product?.description || '',
      deal_image: rawDeal.deal_image || rawDeal.product_image || rawDeal.product?.image || '/logo.png',
      original_price: rawDeal.original_price || rawDeal.product?.price || '0',
      product_slug: rawDeal.product_slug || rawDeal.product?.slug || '',
      availability_percentage: rawDeal.availability_percentage || 0,
      sold_quantity: rawDeal.sold_quantity || 0,
      max_quantity: rawDeal.max_quantity || 0,
    };
  };

  // Fetch banners for hero slideshow
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        setBannersLoading(true);
        const data = await publicApiRequest<any>(API_CONFIG.ENDPOINTS.BANNERS_ACTIVE);
        // API returns raw array (no envelope)
        const items = Array.isArray(data) ? data : [];
        setBanners(sortBannersNewestFirst(items));
        setBannersError(null);
      } catch (error: any) {
        setBannersError('Failed to load banners');
        setBanners([]);
      } finally {
        setBannersLoading(false);
      }
    };
    fetchBanners();
  }, []);

  // Fetch deal of the day
  useEffect(() => {
    const fetchDeal = async () => {
      try {
        setDealLoading(true);
        const data = await publicApiRequest<any>('/api/v1/deals/current/');
        const rawDeal = data?.deal || data?.data?.deal || data?.data || data;

        if (rawDeal && (rawDeal.id || rawDeal.product?.id)) {
          const normalizedDeal = normalizeDeal(rawDeal);
          setDeal(normalizedDeal);
          const remaining = new Date(normalizedDeal.end_time).getTime() - Date.now();
          setTimeLeft(Math.max(0, Math.floor(remaining / 1000)));
          setDealError(null);
        } else {
          setDeal(null);
          setTimeLeft(0);
          setDealError(data.message || 'No active deal');
        }
      } catch (error: any) {
        setDealError('Failed to load deal');
        setDeal(null);
        setTimeLeft(0);
      } finally {
        setDealLoading(false);
      }
    };
    fetchDeal();
  }, []);

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timeLeft]);

  // Auto-advance slideshow every 5 seconds
  useEffect(() => {
    const total = banners.length;
    if (total <= 1) return; // no need to rotate
    const interval = setInterval(() => {
      setCurrentSlide((prevSlide) => (prevSlide === total - 1 ? 0 : prevSlide + 1));
    }, 5000);
    return () => clearInterval(interval);
  }, [banners.length]);

  // Fetch categories data for Home category cards
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await publicApiRequest<any>('/api/v1/shop/categories/');
        // Raw array response (no envelope)
        const categoriesArray = Array.isArray(data) ? data : (data.categories || []);

        const meta: Record<string, { total_items: number; trend?: string }> = {};
        const names: Record<string, string> = {};
        categoriesArray.forEach((category: any) => {
          meta[category.category_name] = {
            total_items: category.item_count,
            trend: category.trend_level
          };
          const name = category.name || category.category_name || category.slug;
          if (name) {
            [name, category.category_name, category.slug, category.display_name]
              .filter(Boolean)
              .forEach((alias: string) => {
                names[alias.toLowerCase().replace(/[^a-z0-9]/g, '')] = name;
              });
          }
        });
        setCategoryMeta(meta);
        setCategoryNames(names);
      } catch (error) {
        // Set default values for all categories
        const defaultMeta: Record<string, { total_items: number; trend?: string }> = {};
        ['phones', 'laptops', 'tablets', 'games', 'smartwatches', 'accessories'].forEach(slug => {
          defaultMeta[slug] = { total_items: 0, trend: undefined };
        });
        setCategoryMeta(defaultMeta);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchWishlistAndCart = async () => {
      const token = localStorage.getItem('authToken');
      const cartToken = cartService.getCartToken();

      // If not logged in and no cart token, skip wishlist and cart API calls
      if (!token && !cartToken) {
        setWishlist([]);
        setCart({});
        return;
      }

      try {
        // Build wishlist URL for guest users with cartToken
        const wishlistUrl = !token && cartToken ? `${API_CONFIG.ENDPOINTS.WISHLIST_ALL}?cart_token=${cartToken}` : '/api/v1/wishlist/';
        const wishlistRes = token
          ? await conditionalApiRequest<any>(wishlistUrl)
          : await publicApiRequest<any>(wishlistUrl);
        const wishlistData = wishlistRes?.data || wishlistRes;
        setWishlist((wishlistData.products || wishlistData.wishlist_items || []).map((p: any) => typeof p === 'number' ? p : (p.product_id || p.id)));
      } catch (error: any) {
        // Only show error if user is actually logged in (has token)
        const token = localStorage.getItem('authToken');
        if (token) {
          showError('Failed to load wishlist', error.message || 'Please try again later.');
        }
      }

      try {
        // Fetch cart on mount (uses authentication if available)
        const cartUrl = !token && cartToken ? `${API_CONFIG.ENDPOINTS.CART_GET}?cart_token=${cartToken}` : '/api/v1/cart/';
        const cartRes = token
          ? await conditionalApiRequest<any>(cartUrl)
          : await publicApiRequest<any>(cartUrl);
        const cartData = cartRes?.data || cartRes;
        const cartMap: Record<number, number> = {};
        (cartData.products || []).forEach((p: any) => { cartMap[p.id] = p.quantity; });
        setCart(cartMap);
      } catch (error: any) {
        const token = localStorage.getItem('authToken');
        if (token) {
          showError('Failed to load cart', error.message || 'Please try again later.');
        }
      }
    };

    fetchWishlistAndCart();
  }, []);

  // Get current products based on active tab
  const getCurrentProducts = () => {
    switch (activeTab) {
      case 'featured':
        return featuredProducts;
      case 'bestsellers':
        return bestSellers;
      case 'new':
        return newArrivals;
      default:
        return featuredProducts;
    }
  };

  const getCurrentLoading = () => {
    switch (activeTab) {
      case 'featured':
        return featuredLoading;
      case 'bestsellers':
        return bestSellersLoading;
      case 'new':
        return newArrivalsLoading;
      default:
        return featuredLoading;
    }
  };

  const getCurrentError = () => {
    switch (activeTab) {
      case 'featured':
        return featuredError;
      case 'bestsellers':
        return bestSellersError;
      case 'new':
        return newArrivalsError;
      default:
        return featuredError;
    }
  };

  const handleAddToCart = async (productId: number) => {
    const token = localStorage.getItem('authToken');

    // Optimistic update
    setCart(prev => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));

    try {
      // Use the appropriate request method based on auth status
      const res = await (token ? apiRequest : publicApiRequest)<any>('/api/v1/cart/add/', {
        method: 'POST',
        body: JSON.stringify({ product_id: productId, quantity: 1 }),
      });
      // optimistic update already applied above
    } catch (error: any) {
      // Revert optimistic update
      setCart(prev => {
        const newCart = { ...prev };
        if (newCart[productId] > 1) {
          newCart[productId]--;
        } else {
          delete newCart[productId];
        }
        return newCart;
      });
      // Only show error if user is actually logged in (has token)
      if (token) {
        showError('Failed to add to cart', error.message || 'Please try again.');
      }
    }
  };

  // Toggle wishlist on single click
  const handleToggleWishlist = async (productId: number, willBeInWishlist?: boolean) => {
    const token = localStorage.getItem('authToken');
    const endpoint = willBeInWishlist ? '/api/v1/wishlist/add/' : '/api/v1/wishlist/remove/';

    // Optimistic update
    setWishlist(prev => willBeInWishlist ? [...prev, productId] : prev.filter(id => id !== productId));

    try {
      const res = await publicApiRequest<any>(endpoint, {
        method: 'POST',
        body: JSON.stringify({ product_id: productId }),
      });
      // optimistic update already applied above
    } catch (error: any) {
      // Revert optimistic update
      setWishlist(prev => willBeInWishlist ? prev.filter(id => id !== productId) : [...prev, productId]);
      // Only show error if user is actually logged in (has token)
      if (token) {
        showError('Failed to update wishlist', error.message || 'Please try again.');
      }
    }
  };

  const handleHeroCTAClick = (slideIndex: number) => {
    const slide = banners[slideIndex];
    const url = slide?.cta_button_url || slide?.link_url;
    navigateToBannerUrl(url);
  };

  const navigateToCategory = (categorySlug: string) => {
    const categoryName = categoryNames[categorySlug.toLowerCase().replace(/[^a-z0-9]/g, '')] || categorySlug;
    navigate(`/categories/${encodeURIComponent(categoryName)}`);
  };

  const navigateToBannerUrl = (url?: string) => {
    if (!url) {
      navigate('/all-products');
      return;
    }

    try {
      const target = new URL(url, window.location.origin);
      if (target.origin === window.location.origin) {
        navigate(`${target.pathname}${target.search}${target.hash}`);
      } else {
        window.location.assign(target.toString());
      }
    } catch {
      navigate('/all-products');
    }
  };

  // Format time as D:HH:MM:SS if days > 0, else HH:MM:SS
  const formatTime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (days > 0) {
      return `${days}d ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="home-page">
      {/* Hero Section - Slideshow Banner */}
      <section className="hero-section">
        <div className="hero-slideshow">
          <div className="slideshow-container">
            {bannersLoading ? (
              <div className="slide-wrapper">
                <div className="slide active">
                  <div className="slide-overlay">
                    <div className="slide-content">
                      <h1>Loading banners…</h1>
                      <p>Please wait.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : bannersError ? (
              <div className="slide-wrapper">
                <div className="slide active">
                  <div className="slide-overlay">
                    <div className="slide-content">
                      <h1>Unable to load banners</h1>
                      <p>Try again later.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : banners.length === 0 ? (
              <div className="slide-wrapper">
                <div className="slide active">
                  <div className="slide-overlay">
                    <div className="slide-content">
                      <h1>No banners yet</h1>
                      <p>Come back soon for exciting offers.</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="slide-wrapper">
                {banners.map((slide, index) => (
                  <div
                    key={slide.id || index}
                    className={`slide ${index === currentSlide ? 'active' : ''}`}
                  >
                    <img src={slide?.image || '/logo.png'} alt={slide?.title || 'Banner'} className="slide-image" onClick={() => navigateToBannerUrl(slide?.cta_button_url || slide?.link_url)} style={{ cursor: 'pointer' }} />
                    <div className="slide-overlay">
                      <div className="slide-content">
                        <h1>{slide?.title || 'Shop Now'}</h1>
                        {slide?.description && <p>{slide.description}</p>}
                        <button className="cta-button" onClick={() => handleHeroCTAClick(index)}>{slide?.cta_button_text || 'Shop Now'}</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Slide indicators */}
            <div className="slide-indicators">
              {banners.map((_, index) => (
                <button
                  key={index}
                  className={`indicator ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Deal of the Day Section */}
      {deal && !dealLoading && (
        <section className="deal-section">
          <div className="container">
            <div className="deal-card">
              <div className="deal-image">
                <img src={deal.deal_image || deal.product_image || deal.product?.image} alt={deal.title} />
                <div className="deal-badge">Deal of the Day</div>
              </div>
              <div className="deal-content">
                <h2>{deal.title}</h2>
                <p>{deal.subtitle}</p>
                <div className="deal-prices">
                  <span className="deal-price">₦{parseFloat(deal.deal_price).toLocaleString()}</span>
                  <span className="original-price">₦{parseFloat(deal.original_price).toLocaleString()}</span>
                  <span className="discount">{deal.discount_percentage}% off</span>
                </div>
                <div className="deal-countdown">
                  <Clock size={20} />
                  <span>{formatTime(timeLeft)}</span>
                </div>
                {deal.max_quantity > 0 && (
                  <div className="deal-availability">
                    <div className="availability-bar">
                      <div className="availability-fill" style={{width: `${deal.availability_percentage}%`}}></div>
                    </div>
                    <span>{deal.sold_quantity}/{deal.max_quantity} sold</span>
                  </div>
                )}
                <button className="deal-btn" onClick={() => deal.product_url ? navigate(deal.product_url) : navigate(`/product/${deal.product_slug || deal.product?.slug}`)}>Shop Now</button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Categories Section */}
      <section className="categories-section">
        <div className="container">
          <h2>Shop by Category</h2>
          <p className="section-subtitle">Discover our wide range of premium gadgets and accessories</p>
          <div className="categories-grid">
            <div className="category-card" onClick={() => navigateToCategory('phones')}>
              <div className="category-image">
                <img
                  src="/phone1.png"
                  alt="Phones"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = 'https://via.placeholder.com/300x200/f3f4f6/9ca3af?text=Phones'; // Cloudinary-style fallback
                  }}
                />
                <div className="category-overlay">
                  <Smartphone className="category-icon" size={32} color="#00C896" />
        </div>
            </div>
            <h3>Phones</h3>
              <p>Latest smart phones and mobile devices.</p>
              
              <button className="shop-now-btn" onClick={() => navigateToCategory('phones')}>Shop Now →</button>
            </div>
            
            <div className="category-card" onClick={() => navigateToCategory('laptops')}>
              <div className="category-image">
                <img src="/laptop1.png" alt="Laptops" />
                <div className="category-overlay">
                  <Laptop className="category-icon" size={32} color="#00C896" />
                </div>
            </div>
            <h3>Laptops</h3>
            <p>High-performance laptops and notebooks</p>
              
              <button className="shop-now-btn" onClick={() => navigateToCategory('laptops')}>Shop Now →</button>
            </div>
            
            <div className="category-card" onClick={() => navigateToCategory('tablets')}>
              <div className="category-image">
              <img src="/tablet.png" alt="Tablets" />
                <div className="category-overlay">
                  <Smartphone className="category-icon" size={32} color="#00C896" />
                </div>
            </div>
            <h3>Tablets</h3>
            <p>iPads, Android Tablets and e-Readers</p>
              
              <button className="shop-now-btn" onClick={() => navigateToCategory('tablets')}>Shop Now →</button>
            </div>
            
            <div className="category-card" onClick={() => navigateToCategory('games')}>
              <div className="category-image">
                <img src="https://images.unsplash.com/photo-1606813907291-d86efa9b94db?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1200&q=80" alt="Games" />
                <div className="category-overlay">
                  <Gamepad2 className="category-icon" size={32} color="#00C896" />
                </div>
            </div>
            <h3>Games</h3>
            <p>Gaming consoles and accessories</p>
              
              <button className="shop-now-btn" onClick={() => navigateToCategory('games')}>Shop Now →</button>
            </div>
            
            <div className="category-card" onClick={() => navigateToCategory('smartwatches')}>
              <div className="category-image">
                <img src="/phonewatch2.png" alt="Smartwatches" />
                <div className="category-overlay">
                  <Watch className="category-icon" size={32} color="#00C896" />
                </div>
            </div>
            <h3>Smartwatches</h3>
            <p>Smart wearables and fitness trackers</p>
              
              <button className="shop-now-btn" onClick={() => navigateToCategory('smartwatches')}>Shop Now →</button>
            </div>
            
            <div className="category-card" onClick={() => navigateToCategory('accessories')}>
              <div className="category-image">
              <img src="/headphone.png" alt="Accessories" />
                <div className="category-overlay">
                  <Headphones className="category-icon" size={32} color="#00C896" />
                </div>
            </div>
            <h3>Accessories</h3>
            <p>Phone cases, chargers, and more</p>
              
              <button className="shop-now-btn" onClick={() => navigateToCategory('accessories')}>Shop Now →</button>
            </div>
          </div>
        </div>
      </section>

      {/* Our Products Section */}
      <section className="products-section">
        <div> {/* Remove className="container" here so products-section is full width */}
          <div className="products-header-row">
            <div className="section-header">
              <h2>Our Products</h2>
            </div>
            <div className="product-tabs">
              <button
                className={`tab-btn ${activeTab === 'featured' ? 'active' : ''}`}
                onClick={() => setActiveTab('featured')}
              >
                <Star size={18} />
                Featured
              </button>
              <button
                className={`tab-btn ${activeTab === 'bestsellers' ? 'active' : ''}`}
                onClick={() => setActiveTab('bestsellers')}
              >
                <Award size={18} />
                Best Sellers
              </button>
              <button
                className={`tab-btn ${activeTab === 'new' ? 'active' : ''}`}
                onClick={() => setActiveTab('new')}
              >
                <Sparkles size={18} />
                New Arrivals
              </button>
            </div>
          </div>
          {/* Products Grid */}
          <div className="products-grid">
            {getCurrentLoading() ? (
              <div className="loading-products">
                <div className="loading-spinner">Loading products...</div>
              </div>
            ) : getCurrentError() ? (
              <div className="error-products">
                <div className="error-message">{getCurrentError()}</div>
              </div>
            ) : getCurrentProducts().length === 0 ? (
              <div className="empty-products">
                <div className="empty-state">
                  {activeTab === 'featured' && (
                    <>
                      <Star size={48} className="empty-icon" />
                      <h3>No Featured Products Yet</h3>
                      <p>We're curating the best products for you. Check back soon!</p>
                    </>
                  )}
                  {activeTab === 'bestsellers' && (
                    <>
                      <Award size={48} className="empty-icon" />
                      <h3>No Best Sellers Yet</h3>
                      <p>Our top-selling products will appear here once available.</p>
                    </>
                  )}
                  {activeTab === 'new' && (
                    <>
                      <Sparkles size={48} className="empty-icon" />
                      <h3>No New Arrivals Yet</h3>
                      <p>Fresh products are on the way. Stay tuned for updates!</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              getCurrentProducts().map((product: any) => (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  slug={product.slug}
                  name={product.name}
                  brand={product.brand_name || product.brand}
                  price={Number(product.discounted_price ?? product.current_price ?? product.price ?? 0)}
                  originalPrice={product.original_price ? Number(product.original_price) : (product.price ? Number(product.price) : undefined)}
                  usdtPrice={product.current_price_usdt || (product.price_usdt ? String(product.price_usdt) : undefined)}
                  originalUsdtPrice={product.original_price_usdt || undefined}
                  rating={4.5} // Default rating since not in API response
                  reviews={0} // Default reviews since not in API response
                  image={product.image || product.main_image || '/logo.png'}
                  badges={
                    product.is_featured ? ['featured'] :
                    product.is_best_seller ? ['best-seller'] :
                    product.is_new_arrival ? ['new-arrival'] : []
                  }
                  inStock={product.is_in_stock}
                  onAddToCart={handleAddToCart}
                  isInCart={cart[product.id] > 0}
                  isInWishlist={wishlist.includes(product.id)}
                  onToggleWishlist={handleToggleWishlist}
                  product_condition={product.product_condition || product.productCondition}
                  productCondition={product.productCondition}
                  condition_display={product.condition_display}
                  is_coupon={product.is_coupon}
                  coupon_value={product.coupon_value}
                />
              ))
            )}
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
